import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { IconButton, PrimaryButton, SecondaryButton } from '../../components/Button'
import Modal, { ModalProps } from '../../components/Modal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInput } from '../../components/Input'
import { ManagedPaymentMethod, getMethod } from '../../util/Managed'
import { formatWithdrawalAccount } from '../../components/Shared'
import User from '../../state/User'
import Checkbox from '../../components/Checkbox'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, shortAddress, successToast, useTestMode } from '../../util/Util'
import { ManagedPaymentMethodSelector } from '../../components/ManagedMethodMultiselector'
import Radom, { WithdrawalAccount } from '../../state/Radom'
import Close from '../../icons/Close'
import NumberInput from '../../components/NumberInput'
import { useNavigate } from 'react-router-dom'

enum TriggerType {
  MinimumAmount,
  TimeInterval,
  WithdrawalAmount
}

interface Trigger {
  triggerType: TriggerType
  triggerName: string
  placeholder: string
  value: string
}

const TriggerTypes = [
  {
    triggerType: TriggerType.MinimumAmount,
    triggerName: 'Minimum amount',
    placeholder: 'Enter amount (USD)',
    value: ''
  },
  {
    triggerType: TriggerType.TimeInterval,
    triggerName: 'Time interval',
    placeholder: 'Select time interval',
    value: ''
  },
  {
    triggerType: TriggerType.WithdrawalAmount,
    triggerName: 'Amount',
    placeholder: 'Enter amount (USD)',
    value: ''
  }
]

const getAvailableTriggerTypes = (triggers: Array<Trigger | null>): typeof TriggerTypes => {
  const selectedTypes = triggers.filter((t): t is Trigger => t !== null).map(t => t.triggerType)
  const hasMinimumAmount = selectedTypes.includes(TriggerType.MinimumAmount)
  const hasTimeInterval = selectedTypes.includes(TriggerType.TimeInterval)
  const hasWithdrawalAmount = selectedTypes.includes(TriggerType.WithdrawalAmount)

  return TriggerTypes.filter(t => {
    if (selectedTypes.includes(t.triggerType)) {
      return false
    }
    if (t.triggerType === TriggerType.WithdrawalAmount && hasMinimumAmount) {
      return false
    }
    if (t.triggerType === TriggerType.MinimumAmount && (hasWithdrawalAmount || hasTimeInterval)) {
      return false
    }
    if (t.triggerType === TriggerType.TimeInterval && hasMinimumAmount) {
      return false
    }
    return true
  })
}

const isValidTriggerCombination = (triggers: Array<Trigger | null>): boolean => {
  const selected = triggers.filter((t): t is Trigger => t !== null)
  if (selected.length === 0) {
    return false
  }

  const types = selected.map(t => t.triggerType)
  const hasWithdrawalAmount = types.includes(TriggerType.WithdrawalAmount)
  const hasTimeInterval = types.includes(TriggerType.TimeInterval)

  if (hasWithdrawalAmount) {
    return hasTimeInterval && selected.length === 2
  }

  return selected.every(t => t.value !== '')
}

const ETHEREUM_USDC_TOKEN = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'
const SEPOLIA_USDC_TOKEN = '0xa4fce8264370437e718ae207805b4e6233638b9e'

type CashWithdrawalCurrency = 'EUR' | 'USD'

const getFiatAccountCurrency = (account: WithdrawalAccount): CashWithdrawalCurrency | undefined => {
  if (account.accountDetails.Fiat?.accountType.iban) {
    return 'EUR'
  }
  if (account.accountDetails.Fiat?.accountType.us) {
    return 'USD'
  }
}

const isAutomationFiatAccountAllowed = (account: WithdrawalAccount): boolean => {
  const fiat = account.accountDetails.Fiat
  if (!fiat) {
    return false
  }
  if (fiat.accountType.iban) {
    return fiat.accountOwnerType?.toLowerCase() === 'business'
  }
  return !!fiat.accountType.us
}

const isFiatFundingAsset = (
  method: ManagedPaymentMethod,
  fiatAccount: WithdrawalAccount,
  testMode: boolean,
  usesLegacyEurOfframp: boolean
): boolean => {
  const fiatCurrency = getFiatAccountCurrency(fiatAccount)
  if (!fiatCurrency) {
    return false
  }

  const network = method.hostChain?.name ?? method.name
  const token = method.hostChain?.tokenAddress
  const assetMethod = getMethod(network, token)
  const tokenAddress = token?.toLowerCase()
  const expectedUsdcNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'
  const expectedUsdcToken = (testMode ? SEPOLIA_USDC_TOKEN : ETHEREUM_USDC_TOKEN).toLowerCase()

  if (fiatCurrency === 'USD') {
    return ['USDC', 'USDT', 'EURC'].includes(assetMethod.ticker)
  }

  if (usesLegacyEurOfframp) {
    return ['USDC', 'USDT', 'EURC'].includes(assetMethod.ticker)
  }

  return network === expectedUsdcNetwork &&
    (assetMethod.ticker === 'USDC' || tokenAddress === expectedUsdcToken)
}

type Props = ModalProps & {
  onAutomationCreated: () => void
}

const AutomationModal = observer(({ onAutomationCreated, ...props }: Props): ReactElement => {
  const navigate = useNavigate()

  const [testMode] = useTestMode()

  const [automationName, setAutomationName] = useState('')
  const [automationType, setAutomationType] = useState('withdrawal')
  const [withdrawalAccount, setWithdrawalAccount] = useState<WithdrawalAccount>()
  const [selectedMethods, setSelectedMethods] = useState<ManagedPaymentMethod[]>([])

  const [isCreatingAutomation, setIsCreatingAutomation] = useState(false)
  const usesLegacyEurOfframp = User.organization?.legacyEurOfframpEnabled === true

  const selectedAssetNetwork = selectedMethods[0]
    ? (selectedMethods[0].hostChain?.name ?? selectedMethods[0].name)
    : undefined
  const cryptoAllowedNetworks = withdrawalAccount?.accountDetails.Fiat || !selectedAssetNetwork
    ? undefined
    : [selectedAssetNetwork]

  useEffect(() => {
    if (selectedMethods.length === 0) {
      setWithdrawalAccount(undefined)
      return
    }

    const selectedMethod = selectedMethods[0]
    setWithdrawalAccount(current => {
      if (!current) {
        return current
      }
      if (current.accountDetails.Fiat) {
        return isFiatFundingAsset(selectedMethod, current, testMode, usesLegacyEurOfframp) ? current : undefined
      }
      const network = selectedMethod.hostChain?.name ?? selectedMethod.name
      if (current.accountDetails.Crypto?.network !== network) {
        return undefined
      }
      return current
    })
  }, [selectedMethods, testMode, usesLegacyEurOfframp])

  useEffect(() => {
    if (!withdrawalAccount?.accountDetails.Fiat || selectedMethods.length === 0) {
      return
    }
    if (!isFiatFundingAsset(selectedMethods[0], withdrawalAccount, testMode, usesLegacyEurOfframp)) {
      setSelectedMethods([])
    }
  }, [withdrawalAccount, selectedMethods, testMode, usesLegacyEurOfframp])

  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])

  const filteredWithdrawalAccounts = withdrawalAccounts.filter(w => {
    if (w.accountDetails.Crypto) {
      const method = getMethod(w.accountDetails.Crypto.network)
      if (testMode ? !method.isTestnet : method.isTestnet) {
        return false
      }
      return cryptoAllowedNetworks === undefined ||
        cryptoAllowedNetworks.includes(w.accountDetails.Crypto.network)
    }

    if (w.accountDetails.Fiat) {
      if (!isAutomationFiatAccountAllowed(w)) {
        return false
      }
      if (selectedMethods.length === 0) {
        return true
      }
      return isFiatFundingAsset(selectedMethods[0], w, testMode, usesLegacyEurOfframp)
    }

    return false
  })

  const paymentMethodFilter = withdrawalAccount?.accountDetails.Fiat
    ? (method: ManagedPaymentMethod) => isFiatFundingAsset(method, withdrawalAccount, testMode, usesLegacyEurOfframp)
    : undefined

  const [triggers, setTriggers] = useState<Array<Trigger | null>>([
    null
  ])

  useEffect(() => {
    // setIsLoadingAccounts(true)
    Radom
      .listWithdrawalAccounts()
      .then(a => setWithdrawalAccounts(a))
      // .finally(() => setIsLoadingAccounts(false))
  }, [])

  const createAutomation = (): void => {
    setIsCreatingAutomation(true)

    const req = {
      name: automationName,
      automationDetails: {
        Withdrawal: {
          assets: selectedMethods.map(m => ({
            network: m.hostChain?.name || m.name,
            token: m.hostChain?.tokenAddress
          })),
          triggers: triggers
            .filter(t => !!t)
            .map((t: Trigger) => {
              switch (t.triggerType) {
              case TriggerType.MinimumAmount:
                return {
                  minimumAmount: {
                    amount: t.value,
                    currency: 'USD'

                  }
                }
              case TriggerType.TimeInterval:
                return {
                  timeInterval: {
                    interval: t.value.toLowerCase()
                  }
                }
              case TriggerType.WithdrawalAmount:
                return {
                  withdrawalAmount: {
                    amount: t.value,
                    currency: 'USD'
                  }
                }
              default:
                return null
              }
            }),
          withdrawalAccountId: withdrawalAccount?.id
        }
      }
    }

    Radom.createAutomation(req)
      .then(() => {
        successToast('Automation created')
        onAutomationCreated()
        props.onClose()
      })
      .catch(err => errorToast(err.response?.data?.error || err.message))
      .finally(() => setIsCreatingAutomation(false))
  }

  return <Modal title='Create automation' {...props}>
    <div style={{ padding: '0 30px', fontSize: 14 }}>

      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 20 }}>

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Automation type</InputLabel>
          <div style={{ fontSize: 14, display: 'flex', gap: 20 }}>
            <SecondaryButton
              disabled={isCreatingAutomation}
              onClick={() => setAutomationType('withdrawal')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: automationType === 'withdrawal' ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox isRadio checked={automationType === 'withdrawal'} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Withdrawal</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                 Set up an automated withdrawal
                </span>
              </div>
            </SecondaryButton>
          </div>
        </div>

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Name</InputLabel>
          <TextInput
            disabled={isCreatingAutomation}
            placeholder='Enter automation name'
            required
            value={automationName}
            onChange={(e) => setAutomationName((e.target as HTMLInputElement).value)} />
        </div>

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <ManagedPaymentMethodSelector
            disabled={isCreatingAutomation}
            label='Asset'
            placeholderText='Select asset'
            selectedMethods={selectedMethods}
            setSelectedMethods={setSelectedMethods}
            allowedNetworks={cryptoAllowedNetworks}
            filterMethod={paymentMethodFilter}
            testnetOnly={testMode} />
        </div>

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Trigger</InputLabel>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '200px 1fr auto',
            backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
            padding: 10,
            borderRadius: 15,
            gap: 10,
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: 450
          }}>
            {
              triggers.map((t, i) => {
                if (t === null) {
                  return <div key={t} style={{ display: 'contents' }}>
                    <Dropdown
                      disabled={isCreatingAutomation}
                      selectedContent={<span>Select trigger</span>}
                      dropdownContent={<>
                        {
                          getAvailableTriggerTypes(triggers)
                            .map(t =>
                              <DropdownItem
                                key={t.triggerName}
                                onClick={() => {
                                  const newTriggers = [...triggers]
                                  newTriggers[i] = { ...t }
                                  setTriggers(newTriggers)
                                }}>
                                {t.triggerName}
                              </DropdownItem>

                            )
                        }
                      </>

                      } />

                    <div></div>
                    <div>

                      {
                        triggers.length > 1 &&
                        <IconButton
                          disabled={isCreatingAutomation}
                          style={{ padding: 0, width: 'fit-content' }}
                          onClick={() => setTriggers([...triggers.slice(0, i), ...triggers.slice(i + 1)]) }>
                          <Close style={{ width: 14, padding: 10 }} />
                        </IconButton>
                      }
                    </div>
                  </div>
                }

                return <>
                  <Dropdown
                    disabled={isCreatingAutomation}
                    selectedContent={<span>{t.triggerName}</span>}
                    dropdownContent={<>
                      {
                        getAvailableTriggerTypes(triggers)
                          .map(t =>
                            <DropdownItem
                              key={t.triggerName}
                              onClick={() => {
                                const newTriggers = [...triggers]
                                newTriggers[i] = { ...t }
                                setTriggers(newTriggers)
                              }}>
                              {t.triggerName}
                            </DropdownItem>

                          )
                      }
                    </>

                    } />

                  {
                    (t.triggerType === TriggerType.MinimumAmount || t.triggerType === TriggerType.WithdrawalAmount) &&
                    <NumberInput
                      disabled={isCreatingAutomation}
                      placeholder={t.placeholder}
                      fontSize='14'
                      value={t.value}
                      onUserInput={(v) => {
                        const newTriggers = [...triggers]
                        newTriggers[i] = { ...t, value: v }
                        setTriggers(newTriggers)
                      }} />
                  }

                  {
                    t.triggerType === TriggerType.TimeInterval &&
                    <Dropdown
                      key={t.value}
                      outerStyle={{ flexGrow: 1 }}
                      disabled={isCreatingAutomation}
                      selectedContent={<span>{ t.value ? t.value : t.placeholder}</span>}
                      dropdownContent={
                        <>
                          {
                            ['Daily', 'Weekly', 'Monthly'].map(value =>
                              <DropdownItem key={t.value}
                                onClick={() => {
                                  const newTriggers = [...triggers]
                                  newTriggers[i] = { ...t, value }
                                  setTriggers(newTriggers)
                                }} >
                                {value}
                              </DropdownItem>
                            )
                          }
                        </>
                      }
                    />
                  }

                  <div>
                    <IconButton
                      disabled={isCreatingAutomation}
                      style={{ padding: 0, width: 'fit-content' }}
                      onClick={() => {
                        if (triggers.length === 1) {
                          return setTriggers([null])
                        }
                        setTriggers([...triggers.slice(0, i), ...triggers.slice(i + 1)])
                      } }>
                      <Close style={{ width: 14, padding: 10 }} />
                    </IconButton>
                  </div>
                </>
              })
            }
          </div>

          <SecondaryButton
            disabled={
              isCreatingAutomation ||
              triggers[triggers.length - 1] === null ||
              triggers.length === 2 ||
              triggers.some(t => t?.triggerType === TriggerType.MinimumAmount) ||
              (triggers.some(t => t?.triggerType === TriggerType.WithdrawalAmount) &&
                triggers.some(t => t?.triggerType === TriggerType.TimeInterval))
            }
            style={{
              border: 0,
              padding: 0,
              color: RADOM_COLORS.BLACK,
              gap: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'fit-content'
            }}
            onClick={() => setTriggers([...triggers, null])}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
            <span>Add trigger</span>
          </SecondaryButton>

          <div>

          </div>

        </div>

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Withdrawal account</InputLabel>
          {
            filteredWithdrawalAccounts.length === 0 &&
            <SecondaryButton
              onClick={() => {
                navigate('/accounts')
                props.onClose()
              }}>
              Create withdrawal account
            </SecondaryButton>
          }
          {
            filteredWithdrawalAccounts.length > 0 &&
            <Dropdown
              disabled={isCreatingAutomation}
              selectedContent={
                withdrawalAccount
                  ? <div
                    style={{
                      fontSize: 14,
                      alignItems: 'center',
                      display: 'flex',
                      gap: 10
                    }}>
                    {
                      withdrawalAccount.accountDetails.Crypto &&
                      <img
                        src={getMethod(withdrawalAccount.accountDetails.Crypto.network).logo}
                        style={{
                          height: 20,
                          width: 'auto',
                          maxWidth: 20
                        }} />
                    }
                    <span>{withdrawalAccount.name}</span>
                    {
                      withdrawalAccount.accountDetails.Fiat &&
                      <span style={{ opacity: 0.5, fontSize: 12 }}>
                        {formatWithdrawalAccount(withdrawalAccount.accountDetails)}
                      </span>
                    }
                  </div>
                  : <span style={{ fontSize: 14 }}>Select account</span>
              }
              dropdownContent={
                filteredWithdrawalAccounts
                  .filter(t => t.id !== withdrawalAccount?.id)
                  .map(t =>
                    <DropdownItem
                      key={t.name}
                      onClick={() => setWithdrawalAccount(t)}
                      style={{
                        fontSize: 14,
                        alignItems: 'center',
                        display: 'flex',
                        gap: 10
                      }}>
                      {
                        t.accountDetails.Crypto &&
                              <img
                                src={getMethod(t.accountDetails.Crypto.network).logo}
                                style={{
                                  height: 20,
                                  width: 'auto',
                                  maxWidth: 20
                                }} />
                      }
                      <span>{t.name}</span>
                      {
                        t.accountDetails.Crypto?.address &&
                        <span style={{ opacity: 0.3 }}>{shortAddress(t.accountDetails.Crypto.address)}</span>
                      }
                      {
                        t.accountDetails.Fiat &&
                        <span style={{ opacity: 0.3, fontSize: 12 }}>
                          {formatWithdrawalAccount(t.accountDetails)}
                        </span>
                      }

                    </DropdownItem>
                  )
              } />
          }
        </div>

      </div>

    </div>

    <div style={{ padding: 30 }}>
      <PrimaryButton
        isLoading={isCreatingAutomation}
        disabled={
          filteredWithdrawalAccounts.length === 0 ||
          !withdrawalAccount ||
          !isValidTriggerCombination(triggers)
        }
        style={{ width: '100%' }}
        onClick={() => {
          createAutomation()
        }}>
        Create automation
      </PrimaryButton>
    </div>
  </Modal>
})

export default AutomationModal
